import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Star } from 'lucide-react';
import { pricingPlans } from '../data/mockData';
import { Link } from 'react-router-dom';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { faqs } from '../data/mockData';

const Pricing = () => {
  return (
    <div className="min-h-screen bg-slate-950 pt-24 pb-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <Badge className="mb-4 bg-amber-500/10 text-amber-400 border-amber-500/20">
            Transparent Pricing
          </Badge>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            Invest in Your Online Success
          </h1>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            Choose the perfect package for your business needs. All plans include
            mobile-responsive design and SEO optimization.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16 max-w-7xl mx-auto">
          {pricingPlans.map((plan) => (
            <Card
              key={plan.id}
              className={`relative bg-slate-800/50 border-slate-700 transition-all duration-300 ${
                plan.popular
                  ? 'border-amber-500 shadow-lg shadow-amber-500/20 md:scale-105'
                  : 'hover:border-amber-500/50'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0 px-4 py-1">
                    <Star className="w-3 h-3 mr-1 fill-white" />
                    Most Popular
                  </Badge>
                </div>
              )}

              <CardContent className="p-8">
                <div className="mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-gray-400 text-sm mb-6">{plan.description}</p>
                  <div className="flex items-baseline mb-8">
                    <span className="text-5xl font-bold text-amber-400">{plan.price}</span>
                    <span className="text-gray-400 ml-2">one-time</span>
                  </div>
                </div>

                <Link to="/contact" className="block">
                  <Button
                    className={`w-full font-semibold py-6 text-lg ${
                      plan.popular
                        ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white shadow-lg shadow-amber-500/20'
                        : 'bg-slate-700 hover:bg-slate-600 text-white'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </Link>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Additional Info */}
        <div className="bg-slate-900/50 rounded-2xl p-8 md:p-12 border border-slate-800 mb-16">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-white mb-4">
              Payment Options
            </h3>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Flexible payment plans to suit your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 text-center">
              <h4 className="text-white font-semibold mb-3 text-lg">50/50 Split</h4>
              <p className="text-gray-400 text-sm">
                Pay 50% upfront to start, 50% upon project completion
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 text-center">
              <h4 className="text-white font-semibold mb-3 text-lg">Full Payment</h4>
              <p className="text-gray-400 text-sm">
                Pay 100% upfront and get started immediately
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-lg p-6 border border-slate-700 text-center">
              <h4 className="text-white font-semibold mb-3 text-lg">Custom Plan</h4>
              <p className="text-gray-400 text-sm">
                Need something different? Let's work out a custom payment schedule
              </p>
            </div>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto mb-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-400">
              Got questions? We've got answers.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={`item-${faq.id}`}
                className="bg-slate-800/50 border border-slate-700 rounded-lg px-6 data-[state=open]:border-amber-500/50"
              >
                <AccordionTrigger className="text-white hover:text-amber-400 text-left">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-gray-400">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-gray-400 mb-8">
            Book a free consultation and let's discuss your project in detail
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold px-8 shadow-lg shadow-amber-500/20"
            >
              Book Free Consultation
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
